import React, { useMemo } from "react";
import "../../styles/Legal.css";

// A markdown renderer for exactly the subset the two legal documents use:
// headings, paragraphs, bullet lists and bold. No links, tables, code or
// images appear in either file, and this deliberately does not support them --
// if the copy grows a link, this throws it away visibly rather than rendering
// something subtly wrong, and that is the failure mode you want in a document
// people are agreeing to be bound by.
//
// Nothing here builds HTML from the source. `dangerouslySetInnerHTML` on a
// document is how a copy edit becomes an XSS hole, and legal text has no
// business being the place that happens.

function Inline({ text }) {
    // Split on **bold**, keeping the delimiters so the pieces alternate.
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return (
        <>
            {parts.map((part, i) =>
                part.startsWith("**") && part.endsWith("**") && part.length > 4 ? (
                    <strong key={i}>{part.slice(2, -2)}</strong>
                ) : (
                    <React.Fragment key={i}>{part}</React.Fragment>
                )
            )}
        </>
    );
}

function parse(markdown) {
    const lines = markdown.replace(/\r\n/g, "\n").split("\n");
    const blocks = [];
    let para = [];
    let list = null;

    const flushPara = () => {
        if (para.length) {
            blocks.push({ type: "p", text: para.join(" ") });
            para = [];
        }
    };
    const flushList = () => {
        if (list) {
            blocks.push({ type: "ul", items: list });
            list = null;
        }
    };

    for (const raw of lines) {
        const line = raw.trimEnd();

        const heading = line.match(/^(#{1,6})\s+(.*)$/);
        if (heading) {
            flushPara();
            flushList();
            blocks.push({ type: "h", level: heading[1].length, text: heading[2] });
            continue;
        }

        const bullet = line.match(/^[*-]\s+(.*)$/);
        if (bullet) {
            flushPara();
            // Trailing "; " and ";" are how these documents punctuate list
            // items. Kept verbatim -- this renders the document, it does not
            // copy-edit it.
            (list = list || []).push(bullet[1]);
            continue;
        }

        if (!line.trim()) {
            flushPara();
            flushList();
            continue;
        }

        flushList();
        para.push(line.trim());
    }
    flushPara();
    flushList();
    return blocks;
}

export default function LegalDoc({ title, lastUpdated, markdown, intro }) {
    const blocks = useMemo(() => {
        // The document opens with its own `# Title` and a `**Last Updated:**`
        // line, and both are rendered in the header above from the exported
        // fields. Dropping them here rather than stripping them in the
        // generator keeps the .js modules a verbatim copy of the .md, which
        // matters when the question is "what exactly did this document say".
        return parse(markdown).filter(
            (b) =>
                !(b.type === "h" && b.level === 1) &&
                !(b.type === "p" && /^\*\*Last Updated:/i.test(b.text))
        );
    }, [markdown]);

    return (
        <article className="legal">
            <header className="legal__head">
                <h1 className="legal__title">{title}</h1>
                {lastUpdated && (
                    <p className="legal__updated">Last updated {lastUpdated}</p>
                )}
                {intro}
            </header>

            <div className="legal__body">
                {blocks.map((block, i) => {
                    if (block.type === "h") {
                        // The document's own `#` is the page title, already
                        // rendered above, so everything shifts down one level:
                        // two <h1>s on a page is a real accessibility problem,
                        // not a styling preference.
                        const Tag = "h" + Math.min(block.level + 1, 6);
                        return (
                            <Tag key={i} className={"legal__h legal__h--" + block.level}>
                                <Inline text={block.text} />
                            </Tag>
                        );
                    }
                    if (block.type === "ul") {
                        return (
                            <ul key={i} className="legal__list">
                                {block.items.map((item, j) => (
                                    <li key={j}><Inline text={item} /></li>
                                ))}
                            </ul>
                        );
                    }
                    return (
                        <p key={i} className="legal__p">
                            <Inline text={block.text} />
                        </p>
                    );
                })}
            </div>
        </article>
    );
}
